import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Alert from '../components/ui/Alert';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import SignPasswordModal from '../components/SignPasswordModal';
import { useTranslation, Trans } from 'react-i18next';
import Modal from '../components/ui/Modal';
import { getCourseDomainStyle } from '../utils/courseDomain';
import { useToast } from '../context/ToastContext';

// SVG Icons
const ClockIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const CheckCircleIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const XCircleIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const TrophyIcon = () => (
  <svg
    className="w-16 h-16 text-accent-500 dark:text-accent-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-4.5A3.375 3.375 0 0019.875 10.875 3.375 3.375 0 0016.5 7.5h0V3.75m-9 15v-4.5A3.375 3.375 0 014.125 10.875 3.375 3.375 0 017.5 7.5h0V3.75m9 0H7.5m9 0h1.875A1.125 1.125 0 0119.5 4.875v1.5A1.125 1.125 0 0118.375 7.5H16.5m-9-3.75H5.625A1.125 1.125 0 004.5 4.875v1.5A1.125 1.125 0 005.625 7.5H7.5"
    />
  </svg>
);

const SadIcon = () => (
  <svg
    className="w-16 h-16 text-danger-500"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.182 16.318A4.486 4.486 0 0012.016 15a4.486 4.486 0 00-3.198 1.318M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z"
    />
  </svg>
);

const BookIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
    />
  </svg>
);

const RefreshIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M2.985 19.644l3.181-3.182"
    />
  </svg>
);

const SendIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
    />
  </svg>
);

const WarningIcon = () => (
  <svg
    className="w-6 h-6 text-warning-500"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
    />
  </svg>
);

const ClipboardIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z"
    />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
  </svg>
);

// Fisher-Yates shuffle
const shuffleArray = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const ONBOARDING_ORDER_TEST = ['IIG_SSM', 'ILM_SSM', 'IIG_SU', 'ILM_SU'];
const TYPE_SHORT_TEST = {
  IIG_SSM: 'IIG SSM',
  ILM_SSM: 'ILM SSM',
  IIG_SU: 'IIG SU',
  ILM_SU: 'ILM SU',
};

// Semnare test inline — angajatul semnează testul după promovare
const TestSignSection = ({ trainingId, courseType }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState('');
  const [nextLink, setNextLink] = useState(null);
  const [nextLabel, setNextLabel] = useState('');

  useEffect(() => {
    // Verifică dacă a semnat deja testul
    api
      .get(`/signatures/test/${trainingId}`)
      .then((res) => {
        const userId = JSON.parse(localStorage.getItem('user') || '{}').id;
        if ((res.data || []).some((s) => s.signer?.id === userId || s.signerId === userId)) {
          setSigned(true);
        }
      })
      .catch(() => {});

    // Calculează next course
    const idx = ONBOARDING_ORDER_TEST.indexOf(courseType);
    if (idx >= 0 && idx < ONBOARDING_ORDER_TEST.length - 1) {
      api
        .get('/courses/my/assigned')
        .then((res) => {
          const assignments = res.data || [];
          for (let i = idx + 1; i < ONBOARDING_ORDER_TEST.length; i++) {
            const nextType = ONBOARDING_ORDER_TEST[i];
            const next = assignments.find((a) => a.course?.type === nextType);
            if (next && next.training?.status !== 'PASSED') {
              setNextLink(`/training/${next.course.id}`);
              setNextLabel(t('test.continueTo', { type: TYPE_SHORT_TEST[nextType] }));
              return;
            }
          }
        })
        .catch(() => {});
    }
  }, [trainingId, courseType]);

  const [showPwdModal, setShowPwdModal] = useState(false);
  const handleSign = () => {
    setError('');
    setShowPwdModal(true);
  };
  const submitSign = async (password) => {
    setSigning(true);
    try {
      await api.post('/signatures/test', { trainingId, password });
      setSigned(true);
      setShowPwdModal(false);
    } catch (err) {
      const data = err.response?.data;
      if (data?.requiresAdESActivation) {
        setShowPwdModal(false);
        setError(data.error || t('test.activateAdES'));
        return;
      }
      throw err;
    } finally {
      setSigning(false);
    }
  };

  if (signed) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 justify-center text-accent-700 dark:text-accent-300">
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="font-medium">{t('test.testSignedSuccess')}</span>
        </div>
        <div className="flex gap-3 justify-center flex-wrap">
          {nextLink && (
            <Button variant="accent" size="lg" onClick={() => navigate(nextLink)}>
              {nextLabel} →
            </Button>
          )}
          <Button
            variant={nextLink ? 'ghost' : 'primary'}
            size={nextLink ? 'sm' : 'lg'}
            onClick={() => navigate(nextLink ? '/my-courses' : '/sheets-to-sign')}
          >
            {nextLink ? t('test.backToCourses') : t('test.backToSheets')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {showPwdModal && (
        <SignPasswordModal
          description={t('test.signTestModalDesc')}
          onSubmit={submitSign}
          onClose={() => setShowPwdModal(false)}
        />
      )}
      <p className="text-accent-900 dark:text-accent-200 font-medium text-sm">
        {t('test.signTestPrompt')}
      </p>
      {error && <p className="text-danger-600 dark:text-danger-300 text-xs">{error}</p>}
      <Button variant="accent" size="lg" onClick={handleSign} loading={signing}>
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
          />
        </svg>
        {signing ? t('test.signing') : t('test.signTestBtn')}
      </Button>
    </div>
  );
};

const TestPage = () => {
  const { t } = useTranslation();
  const { trainingId } = useParams();
  const navigate = useNavigate();

  const [training, setTraining] = useState(null);
  const { showToast } = useToast();
  const [answers, setAnswers] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(`test-answers-${trainingId}`)) || {};
    } catch {
      return {};
    }
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // #13: pop-up de felicitare la promovare — apare DOAR după trimiterea testului (nu în review mode).
  const [showPassModal, setShowPassModal] = useState(false);
  const [unansweredCount, setUnansweredCount] = useState(0);
  const [shuffledQuestions, setShuffledQuestions] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(`test-shuffle-${trainingId}`));
    } catch {
      return null;
    }
  });
  // Preferință cursant: amestecă ordinea răspunsurilor/întrebărilor. Default: OFF (ordine originală).
  const [shufflePref, setShufflePref] = useState(() => {
    try {
      return localStorage.getItem('test-shuffle-pref') === '1';
    } catch {
      return false;
    }
  });

  // Persistă răspunsurile în localStorage
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem(`test-answers-${trainingId}`, JSON.stringify(answers));
    }
  }, [answers, trainingId]);

  // Persistă preferința de amestecare
  useEffect(() => {
    try {
      localStorage.setItem('test-shuffle-pref', shufflePref ? '1' : '0');
    } catch {}
  }, [shufflePref]);

  // Construiește ordinea întrebărilor/răspunsurilor în funcție de preferință.
  // shuffle=false → ordinea originală (fără amestecare). shuffle=true → Fisher-Yates.
  const buildQuestions = (questions, shuffle) => {
    if (!shuffle) {
      return (questions || []).map((q) => ({ ...q, answers: [...(q.answers || [])] }));
    }
    return shuffleArray(questions || []).map((q) => ({
      ...q,
      answers: shuffleArray(q.answers || []),
    }));
  };

  useEffect(() => {
    fetchTraining();
  }, [trainingId]);

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const fetchTraining = async () => {
    try {
      const res = await api.get(`/trainings/${trainingId}`);
      setTraining(res.data);

      // Dacă testul e deja promovat — arată rezultatele direct (review mode)
      if (res.data.passed) {
        const testResult = res.data.testResults?.[0];
        setResult({
          passed: true,
          score: res.data.score,
          message: t('test.scoreLabel', { score: res.data.score }),
        });
        // Restaurează răspunsurile din testResult
        if (testResult?.answers) setAnswers(testResult.answers);
        setLoading(false);
        return;
      }

      // Timer test OPȚIONAL: doar dacă instructorul a setat o limită pe curs (testTimeLimit, minute).
      // Dacă e null → fără cronometru, fără presiune de timp (comportament implicit).
      const testLimitMin = res.data.course?.testTimeLimit;
      setTimeLeft(testLimitMin ? testLimitMin * 60 : null);
      if (!shuffledQuestions) {
        const questions = res.data.course?.finalTest?.questions || [];
        const ordered = buildQuestions(questions, shufflePref);
        setShuffledQuestions(ordered);
        localStorage.setItem(`test-shuffle-${trainingId}`, JSON.stringify(ordered));
      }
    } catch (err) {
      showToast(t('test.errorLoading'), 'error');
      navigate('/my-courses');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId, answerId, isMultiple) => {
    if (isMultiple) {
      const curr = answers[questionId] || [];
      setAnswers({
        ...answers,
        [questionId]: curr.includes(answerId)
          ? curr.filter((id) => id !== answerId)
          : [...curr, answerId],
      });
    } else {
      setAnswers({ ...answers, [questionId]: answerId });
    }
  };

  const handleSubmit = async () => {
    const finalQuestions = training?.course?.finalTest?.questions || [];
    const unanswered = finalQuestions.filter((q) => {
      const a = answers[q.id];
      return !a || (Array.isArray(a) && a.length === 0);
    });

    if (unanswered.length > 0 && timeLeft > 0) {
      setUnansweredCount(unanswered.length);
      setShowConfirmModal(true);
      return;
    }

    await doSubmit();
  };

  const doSubmit = async () => {
    setShowConfirmModal(false);
    setSubmitting(true);
    try {
      const res = await api.post(`/trainings/${trainingId}/test`, { answers });
      setResult(res.data);
      if (res.data?.passed) setShowPassModal(true);
      // Curăță localStorage după submit
      localStorage.removeItem(`test-answers-${trainingId}`);
      localStorage.removeItem(`test-shuffle-${trainingId}`);
    } catch (err) {
      showToast(err.response?.data?.error || t('test.errorSubmitting'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <Spinner size="lg" label={t('test.loading')} />;
  }

  const showCorrect = training?.course?.showCorrectAnswers !== false;
  // Diferențiere vizuală pe domeniu: SSM albastru / SU roșu (tentă ușoară + badge).
  const domainStyle = getCourseDomainStyle(training?.course?.type);
  const DomainBanner = () => (
    <div className={`rounded-lg px-4 py-2 flex items-center gap-2 ${domainStyle.cardTint}`}>
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${domainStyle.badgeClass}`}
      >
        {domainStyle.domain}
      </span>
      <span className="text-xs text-slate-600 dark:text-slate-300">{domainStyle.label}</span>
    </div>
  );

  // === ECRAN REZULTAT ===
  if (result) {
    const finalQuestions = training?.course?.finalTest?.questions || [];

    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <DomainBanner />
        {/* #13: pop-up de felicitare la promovare — direcționează userul către SEMNARE,
            nu către certificate. Anterior butonul „Vezi certificatele" îl scotea din flow
            și uita să semneze fișa, lăsând instruirea în limbo. Acum singura cale „înainte"
            e semnarea (sau Continuă, care doar închide popup-ul). */}
        <Modal
          open={showPassModal}
          onClose={() => setShowPassModal(false)}
          title={t('test.passModalTitle')}
          footer={
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setShowPassModal(false)}>
                {t('test.passModalContinue')}
              </Button>
              <Button
                variant="accent"
                onClick={() => {
                  setShowPassModal(false);
                  navigate(`/training/${trainingId}/sign`, { state: { scrollTo: 'test' } });
                }}
              >
                {t('test.passModalGoSign')}
              </Button>
            </div>
          }
        >
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="w-8 h-8 text-accent-500 flex-shrink-0" />
            <p className="text-sm text-slate-700 dark:text-slate-300">
              {t('test.passModalBody', { score: result.score })}
            </p>
          </div>
        </Modal>

        {/* Score Card */}
        <Card
          className={`text-center ${result.passed ? 'ring-2 ring-accent-200 dark:ring-1 dark:ring-accent-800 bg-accent-50/50 dark:bg-accent-900/20' : 'ring-2 ring-danger-200 dark:ring-danger-700 bg-danger-50/50 dark:bg-danger-950/50'}`}
        >
          <div className="py-4">
            <div className="flex justify-center mb-4">
              {result.passed ? <TrophyIcon /> : <SadIcon />}
            </div>
            <h1
              className={`text-2xl sm:text-3xl font-bold mb-3 ${result.passed ? 'text-accent-900 dark:text-accent-200' : 'text-danger-900 dark:text-danger-300'}`}
            >
              {result.passed ? t('test.passedHeading') : t('test.failedHeading')}
            </h1>
            <div
              className={`text-6xl sm:text-7xl font-bold mb-4 tabular-nums ${result.passed ? 'text-accent-600 dark:text-accent-400' : 'text-danger-600 dark:text-danger-300'}`}
            >
              {result.score}%
            </div>
            <p
              className={`text-base mb-8 ${result.passed ? 'text-accent-800 dark:text-accent-300' : 'text-danger-800 dark:text-danger-300'}`}
            >
              {result.message}
            </p>

            {result.passed ? (
              (() => {
                const type = training?.course?.type || '';
                const isIIGorILM = ['IIG_SSM', 'ILM_SSM', 'IIG_SU', 'ILM_SU'].includes(type);
                const currentUserId = JSON.parse(localStorage.getItem('user') || '{}').id;
                const isOwner = training?.employee?.userId === currentUserId;

                return isIIGorILM ? (
                  <TestSignSection trainingId={trainingId} courseType={type} />
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 justify-center text-accent-700 dark:text-accent-300">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="font-medium">{t('test.testPassedShort')}</span>
                    </div>
                    <Button
                      variant="accent"
                      size="lg"
                      onClick={() => navigate(isOwner ? '/my-courses' : '/sheets-to-sign')}
                    >
                      {isOwner ? t('test.backToCourses') : t('test.backToSheets')}
                    </Button>
                  </div>
                );
              })()
            ) : (
              <div className="space-y-3">
                <p className="text-danger-800 dark:text-danger-300 text-sm">
                  {t('test.retryOptions')}
                </p>
                <label className="flex items-center justify-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={shufflePref}
                    onChange={(e) => setShufflePref(e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded border-slate-300 dark:border-slate-700"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {t('test.shuffleAnswers')}
                  </span>
                </label>
                <div className="flex gap-3 justify-center flex-wrap">
                  <Button
                    variant="primary"
                    onClick={async () => {
                      try {
                        await api.post(`/trainings/${trainingId}/reset`);
                        navigate(`/training/${training.courseId}?fromTest=${trainingId}`);
                      } catch (err) {
                        showToast(err.response?.data?.error || t('test.errorReset'), 'error');
                      }
                    }}
                  >
                    <BookIcon />
                    {t('test.restartCourse')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setResult(null);
                      setAnswers({});
                      localStorage.removeItem(`test-answers-${trainingId}`);
                      localStorage.removeItem(`test-shuffle-${trainingId}`);
                      const questions = training?.course?.finalTest?.questions || [];
                      const ordered = buildQuestions(questions, shufflePref);
                      setShuffledQuestions(ordered);
                      localStorage.setItem(`test-shuffle-${trainingId}`, JSON.stringify(ordered));
                    }}
                  >
                    <RefreshIcon />
                    {t('test.retryTest')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Recapitulare raspunsuri */}
        <Card>
          <div className="flex items-center gap-2 mb-5">
            <ClipboardIcon />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('test.yourAnswers')}
            </h2>
          </div>
          <div className="space-y-4">
            {finalQuestions.map((q, idx) => {
              const userAns = answers[q.id];
              const userArr = Array.isArray(userAns) ? userAns : [userAns];
              const correctAnswers = q.answers.filter((a) => a.isCorrect);
              const correctIds = new Set(correctAnswers.map((a) => a.id));
              const userSet = new Set(userArr.filter(Boolean));
              const isOk =
                userSet.size > 0 &&
                [...correctIds].every((id) => userSet.has(id)) &&
                [...userSet].every((id) => correctIds.has(id));

              return (
                <div
                  key={q.id}
                  className={`p-4 rounded-lg border-2 ${isOk ? 'border-accent-200 dark:border-accent-700 bg-accent-50 dark:bg-accent-950/50' : 'border-danger-200 dark:border-danger-700 bg-danger-50 dark:bg-danger-950/50'}`}
                >
                  <div className="flex items-start gap-2 mb-3">
                    <span className="flex-shrink-0 mt-0.5">
                      {isOk ? (
                        <CheckCircleIcon className="w-5 h-5 text-accent-600 dark:text-accent-300" />
                      ) : (
                        <XCircleIcon className="w-5 h-5 text-danger-600 dark:text-danger-300" />
                      )}
                    </span>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      <span className="text-slate-500 dark:text-slate-400 mr-1">{idx + 1}.</span>
                      {q.text}
                    </p>
                  </div>
                  <div className="space-y-1.5 ml-7">
                    {q.answers.map((a) => {
                      const wasSelected = userArr.includes(a.id);
                      const isCorrect = a.isCorrect;
                      return (
                        <div
                          key={a.id}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs ${
                            showCorrect && isCorrect && wasSelected
                              ? 'bg-accent-200 dark:bg-accent-900/50 text-accent-800 dark:text-accent-300 font-semibold'
                              : showCorrect && isCorrect
                                ? 'bg-accent-100 dark:bg-accent-950/50 text-accent-700 dark:text-accent-300 font-semibold'
                                : wasSelected && !isCorrect
                                  ? 'bg-danger-200 dark:bg-danger-900/50 text-danger-800 dark:text-danger-300'
                                  : wasSelected && isCorrect
                                    ? 'bg-accent-200 dark:bg-accent-900/50 text-accent-800 dark:text-accent-300 font-semibold'
                                    : 'text-slate-600 dark:text-slate-300'
                          }`}
                        >
                          <span className="flex-shrink-0">
                            {showCorrect && isCorrect ? (
                              <CheckCircleIcon className="w-3.5 h-3.5" />
                            ) : wasSelected && isCorrect ? (
                              <CheckCircleIcon className="w-3.5 h-3.5" />
                            ) : wasSelected ? (
                              <XCircleIcon className="w-3.5 h-3.5" />
                            ) : (
                              <span className="inline-block w-3.5 h-3.5 rounded-full border border-slate-300 dark:border-slate-700" />
                            )}
                          </span>
                          <span>{a.text}</span>
                          {showCorrect && isCorrect && (
                            <span className="ml-auto text-xs font-bold">
                              {t('test.correctTag')}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    );
  }

  const finalQuestions = shuffledQuestions || training?.course?.finalTest?.questions || [];

  if (finalQuestions.length === 0) {
    // Cursul nu are test — dacă e deja promovat, mergi direct la semnătură
    if (training?.passed) {
      navigate(`/training/${trainingId}/sign`);
      return null;
    }
    return (
      <div className="max-w-3xl mx-auto">
        <Alert type="warning" className="text-center">
          <div className="py-4">
            <h2 className="text-xl font-bold text-warning-900 dark:text-warning-300 mb-2">
              {t('test.testNotConfigured')}
            </h2>
            <p className="text-warning-800 dark:text-warning-300 mb-6">{t('test.contactAdmin')}</p>
            <Button variant="outline" onClick={() => navigate('/my-courses')}>
              <ArrowLeftIcon />
              {t('test.backToCoursesPlain')}
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  const answeredCount = Object.keys(answers).filter((k) => {
    const v = answers[k];
    return v && (!Array.isArray(v) || v.length > 0);
  }).length;
  const progressPct = Math.round((answeredCount / finalQuestions.length) * 100);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <DomainBanner />
      {/* Header test */}
      <PageHeader
        title={`${t('test.title')}: ${training.course.title}`}
        subtitle={`${answeredCount}/${finalQuestions.length}  ·  ${t('test.score')}: ${training.course.finalTest?.passingScore || 80}%`}
        actions={
          timeLeft !== null && (
            <div
              className={`text-right flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                timeLeft < 30
                  ? 'bg-danger-50 dark:bg-danger-950/50'
                  : timeLeft < 60
                    ? 'bg-danger-50 dark:bg-danger-950/50'
                    : ''
              }`}
            >
              <ClockIcon />
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {t('test.timeLeft')}
                </div>
                <div
                  className={`text-2xl font-bold font-mono tabular-nums ${
                    timeLeft < 30
                      ? 'text-danger-600 dark:text-danger-300 animate-pulse'
                      : timeLeft < 60
                        ? 'text-danger-600 dark:text-danger-300'
                        : timeLeft < 300
                          ? 'text-warning-600 dark:text-warning-300'
                          : 'text-primary-600 dark:text-primary-300'
                  }`}
                >
                  {formatTime(timeLeft)}
                </div>
              </div>
            </div>
          )
        }
      >
        {/* Toggle amestecare ordine răspunsuri — vizibil la începutul testului */}
        <div className="mt-3">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={shufflePref}
              onChange={(e) => {
                const on = e.target.checked;
                setShufflePref(on);
                // Re-generează imediat ordinea pe testul curent (înainte de submit)
                const questions = training?.course?.finalTest?.questions || [];
                const ordered = buildQuestions(questions, on);
                setShuffledQuestions(ordered);
                localStorage.setItem(`test-shuffle-${trainingId}`, JSON.stringify(ordered));
              }}
              className="w-4 h-4 text-primary-600 rounded border-slate-300 dark:border-slate-700"
            />
            <span className="text-sm text-slate-600 dark:text-slate-300">
              {t('test.shuffleAnswers')}
            </span>
          </label>
        </div>

        {/* Progress bar */}
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <Badge color={answeredCount === finalQuestions.length ? 'green' : 'blue'}>
            {answeredCount}/{finalQuestions.length}
          </Badge>
        </div>
      </PageHeader>

      {/* Intrebari */}
      {finalQuestions.map((question, index) => {
        const hasAnswer = (() => {
          const a = answers[question.id];
          return a && (!Array.isArray(a) || a.length > 0);
        })();

        return (
          <Card key={question.id}>
            <div className="flex items-start gap-4 mb-5">
              <div
                className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                  hasAnswer
                    ? 'bg-accent-100 dark:bg-accent-950/50 text-accent-700 dark:text-accent-300'
                    : 'bg-primary-50 dark:bg-primary-950/50 text-primary-700 dark:text-primary-300'
                }`}
              >
                {index + 1}
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  {question.text}
                </h3>
                <p className="text-xs text-slate-400 dark:text-slate-400 mt-1">
                  {question.type === 'multiple_choice'
                    ? t('test.multipleChoice')
                    : t('test.singleChoice')}
                </p>
              </div>
            </div>

            <div className="space-y-2" style={{ marginLeft: '3.25rem' }}>
              {question.answers.map((answer) => {
                const isSelected =
                  question.type === 'multiple_choice'
                    ? (answers[question.id] || []).includes(answer.id)
                    : answers[question.id] === answer.id;

                return (
                  <label
                    key={answer.id}
                    className={`flex items-center gap-3 p-3.5 border-2 rounded-xl cursor-pointer transition-all select-none ${
                      isSelected
                        ? 'border-primary-600 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/50'
                        : 'border-slate-200 dark:border-slate-800 hover:border-primary-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <input
                      type={question.type === 'multiple_choice' ? 'checkbox' : 'radio'}
                      name={`q-${question.id}`}
                      checked={isSelected}
                      onChange={() =>
                        handleAnswerSelect(
                          question.id,
                          answer.id,
                          question.type === 'multiple_choice'
                        )
                      }
                      className="h-4 w-4 text-primary-600 flex-shrink-0"
                    />
                    <span
                      className={`text-sm ${isSelected ? 'text-primary-900 dark:text-primary-200 font-medium' : 'text-slate-700 dark:text-slate-300'}`}
                    >
                      {answer.text}
                    </span>
                  </label>
                );
              })}
            </div>
          </Card>
        );
      })}

      {/* Submit */}
      <Card className="sticky bottom-4 z-10 shadow-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            {answeredCount < finalQuestions.length ? (
              <div className="flex items-center gap-2 text-sm text-warning-700 dark:text-warning-300 font-medium">
                <WarningIcon />
                {t('test.unansweredQuestions', { count: finalQuestions.length - answeredCount })}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-accent-700 dark:text-accent-300 font-medium">
                <CheckCircleIcon className="w-5 h-5" />
                {t('test.allAnswered')}
              </div>
            )}
            <p className="text-xs text-slate-400 dark:text-slate-400 mt-1">
              {t('test.minPassingScore', {
                score: training.course.finalTest?.passingScore || 80,
              })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/training/${training.courseId}?fromTest=${trainingId}`)}
            >
              <ArrowLeftIcon />
              {t('test.backToCourse')}
            </Button>
            <Button
              variant="accent"
              size="lg"
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting}
            >
              <SendIcon />
              {submitting ? t('test.submitting') : t('test.submitTest')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Modal confirmare trimitere cu intrebari necompletate */}
      <Modal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={t('test.confirmSubmit')}
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowConfirmModal(false)}>
              {t('test.backToTestPlain')}
            </Button>
            <Button variant="accent" onClick={doSubmit}>
              <SendIcon />
              {t('test.submitAnyway')}
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-3">
          <WarningIcon />
          <div>
            <p className="text-sm text-slate-700 dark:text-slate-300">
              <Trans
                i18nKey="test.unansweredConfirm"
                count={unansweredCount}
                values={{ count: unansweredCount }}
                components={{ b: <strong className="text-warning-700 dark:text-warning-300" /> }}
              />
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('test.sureSubmit')}
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TestPage;
